import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { Toast } from "@/components/toast";

type ToastTone = "error" | "success" | "info" | "warning";

interface ToastItem {
  id: number;
  title: string;
  message: string;
  tone: ToastTone;
}

interface ToastActions {
  showAlert: (title: string, message: string, tone?: ToastTone) => void;
  showConfirm: (
    title: string,
    message: string,
    onConfirm: () => void,
    onCancel?: () => void,
  ) => void;
}

const ToastContext = createContext<ToastActions | null>(null);

let nextId = 0;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const showAlert = useCallback((title: string, message: string, tone: ToastTone = "error") => {
    const id = nextId++;
    setToasts((prev) => [...prev, { id, title, message, tone }]);
  }, []);

  const showConfirm = useCallback(
    (title: string, message: string, onConfirm: () => void, onCancel?: () => void) => {
      if (typeof window !== "undefined" && window.confirm(`${title}\n\n${message}`)) {
        onConfirm();
      } else {
        onCancel?.();
      }
    },
    [],
  );

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const value = useMemo(() => ({ showAlert, showConfirm }), [showAlert, showConfirm]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toasts.map((t) => (
        <Toast
          key={t.id}
          title={t.title}
          message={t.message}
          tone={t.tone}
          onDismiss={() => dismissToast(t.id)}
        />
      ))}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastActions {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}
